import { ChangeDetectionStrategy, Component, input } from '@angular/core';
import {
  CrownIconComponent,
  LayersIconComponent,
  OrgChartIconComponent,
  ReceiptIconComponent,
  SettingIconComponent,
  ShieldTickIconComponent,
  ShieldUserIconComponent,
  TableSettingsIconComponent,
  TagRightIconComponent,
  UsersGroupIconComponent,
  UsersIconComponent,
  DocumentIconComponent,
  VendorDataIconComponent,
  VendorPermissionIconComponent,
  WalletIconComponent,
} from '@kuiper/icons';

export type SideMenuIconName =
  | 'home'
  | 'tasks'
  | 'calendar'
  | 'workflow'
  | 'add'
  | 'chevron'
  | 'folder'
  | 'package'
  | 'vendors'
  | 'invoice'
  | 'borrowing'
  | 'clock'
  | 'team'
  | 'building'
  | 'network'
  | 'bell'
  | 'sidebar'
  | 'user-octagon'
  // App-library icons for pages Figma doesn't draw (admin)
  | 'users'
  | 'layers'
  | 'users-group'
  | 'tag'
  | 'org-chart'
  | 'shield-user'
  | 'crown'
  | 'document'
  | 'shield-tick'
  | 'table-settings'
  | 'vendor-permission'
  | 'vendor-data'
  | 'receipt'
  | 'wallet'
  | 'setting';

/** Menu item id -> the icon Figma draws for it. Items not listed keep their own iconTemplate. */
export const SIDE_MENU_ITEM_ICONS: Record<string, SideMenuIconName> = {
  home: 'home',
  inbox: 'tasks',
  businessCalendar: 'calendar',
  requests: 'workflow',
  project: 'package',
  vendors: 'vendors',
  invoice: 'invoice',
  corporateFinanceBorrowings: 'borrowing',
  corporateFinanceTimeDeposits: 'clock',
  team: 'team',
  organization: 'building',
  organizationsHierarchy: 'network',
  // Admin (no Figma frame): app-library icons; owner/category pages reuse Figma's folder and workflow.
  usersDatabase: 'users',
  departments: 'layers',
  groups: 'users-group',
  jobTitles: 'tag',
  positions: 'org-chart',
  admins: 'shield-user',
  superManagers: 'crown',
  usersVariables: 'document',
  productsOwnerType: 'folder',
  productsPermissions: 'shield-tick',
  productsVariables: 'document',
  workflowsOwnerCategory: 'workflow',
  workflowsPermissions: 'shield-tick',
  workflowsConfigurations: 'table-settings',
  workflowsVariables: 'document',
  vendorPermissions: 'vendor-permission',
  vendorDatabase: 'vendor-data',
  sadadBillers: 'receipt',
  subscription: 'wallet',
  general: 'setting',
};

/**
 * KUIP-68 side-menu icons. Most are exported from the Figma frame (DIB UI - Employee, node 23070-524435)
 * and kept in step with design-system/prototypes/side-menu/src/figma-icons.html.
 * Nav icons (home through network) are Lucide outlines drawn with their own 2px stroke;
 * the same shape at rest and active, only the colour changes.
 * Admin pages have no Figma frame; they use @kuiper/icons components (the cases after user-octagon).
 */
@Component({
  selector: 'lib-side-menu-icon',
  imports: [
    UsersIconComponent,
    LayersIconComponent,
    UsersGroupIconComponent,
    TagRightIconComponent,
    OrgChartIconComponent,
    ShieldUserIconComponent,
    CrownIconComponent,
    DocumentIconComponent,
    ShieldTickIconComponent,
    TableSettingsIconComponent,
    VendorPermissionIconComponent,
    VendorDataIconComponent,
    ReceiptIconComponent,
    WalletIconComponent,
    SettingIconComponent,
  ],
  templateUrl: './side-menu-icon.component.html',
  host: { class: 'inline-flex shrink-0', 'aria-hidden': 'true' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SideMenuIconComponent {
  readonly name = input.required<SideMenuIconName>();
  readonly iconClass = input<string>('size-5');
  /** Picks the bulk variant of app sidenav icons that have one. */
  readonly active = input<boolean>(false);
}
